import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import AppPaths from '../../AppPaths';
import { QUIZ_URL_TRIGGER } from '../../lib/fiQuiz';
import { HARDWARE_FIRST_VARIANT, parseQuizVariantOverride } from '../../lib/fiQuizOverride';
import HardwareFirstQuiz from './HardwareFirstQuiz';

type ShopDeviceKey = 'mini' | 's3';

interface HardwareFirstQuizApi {
  /** `trigger` names the CTA that opened the quiz, for analytics. */
  open(trigger: string): void;
  close(): void;
  /** Set when the quiz's result CTA is tapped while the visitor is on the shop. */
  pendingDevice: ShopDeviceKey | null;
  /** Applies `pendingDevice` once, so a re-render does not re-select it. */
  consumePendingDevice(): void;
}

const noop: HardwareFirstQuizApi = {
  open: () => undefined,
  close: () => undefined,
  pendingDevice: null,
  consumePendingDevice: () => undefined,
};

const HardwareFirstQuizContext = createContext<HardwareFirstQuizApi>(noop);

/**
 * Mounts the quiz sheet once for the whole app so any CTA can open it without
 * rendering a copy of its own. `useLaunchQuiz` is the only caller that should
 * decide *whether* this quiz is the right one for a visitor.
 */
export function HardwareFirstQuizProvider({ children }: { children: React.ReactNode }) {
  const [trigger, setTrigger] = useState<string | null>(null);
  const { search, pathname } = useLocation();
  const history = useHistory();
  const [pendingDevice, setPendingDevice] = useState<ShopDeviceKey | null>(null);
  const isOpen = trigger !== null;

  const open = useCallback((source: string) => setTrigger(source), []);
  const close = useCallback(() => setTrigger(null), []);

  // Locks the page behind the sheet. reactjs-popup ships a `lockScroll` prop, but it sets
  // `overflow: auto` on close rather than restoring what was there, and safari ignores
  // `overflow: hidden` on body outright - so we pin the body the way the nav drawer does
  // and put the scroll position back when the sheet closes.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const { scrollY } = window;
    const previous = {
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      document.body.style.position = previous.position;
      document.body.style.top = previous.top;
      document.body.style.width = previous.width;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  // Opens the sheet on arrival for `?quizVariant=hardware-first`, so it can be
  // exercised in prod before it is wired into the CTAs.
  useEffect(() => {
    if (parseQuizVariantOverride(search) === HARDWARE_FIRST_VARIANT) {
      setTrigger(QUIZ_URL_TRIGGER);
    }
  }, [search]);

  // The shop holds every tracker on one page. A visitor already on it gets an
  // in-place selection - pushing `?device=` would not re-select, since the page
  // reads that param only on mount. From anywhere else, navigate.
  const continueWithDevice = useCallback(
    (deviceKey: ShopDeviceKey) => {
      close();
      if (pathname.endsWith(AppPaths.Shop)) {
        setPendingDevice(deviceKey);
        return;
      }
      history.push({ pathname: AppPaths.Shop, search: `?device=${deviceKey}` });
    },
    [close, history, pathname],
  );

  const consumePendingDevice = useCallback(() => setPendingDevice(null), []);

  const api = useMemo<HardwareFirstQuizApi>(
    () => ({ open, close, pendingDevice, consumePendingDevice }),
    [open, close, pendingDevice, consumePendingDevice],
  );

  return (
    <HardwareFirstQuizContext.Provider value={api}>
      {children}
      <HardwareFirstQuiz open={isOpen} onClose={close} onContinue={continueWithDevice} />
    </HardwareFirstQuizContext.Provider>
  );
}

export function useHardwareFirstQuiz(): HardwareFirstQuizApi {
  return useContext(HardwareFirstQuizContext);
}
